import { useCallback, useEffect, useState } from 'react';
import {
  getAthenaNotificationStatus,
  requestAthenaNotificationsPermission,
} from '../notifications/AthenaNotifications';
import {
  enableAthenaFloatingOverlay,
  getAthenaFloatingOverlayStatus,
  isAthenaFloatingOverlayAvailable,
  requestAthenaFloatingOverlayPermission,
  setAthenaFloatingOverlayInteractionLevel,
} from '../platform/AthenaFloatingOverlay';
import { isAndroid } from '../platform/NativeBridge';

const SETUP_KEY = 'athena_quick_setup_v1';
const NOTIFICATION_STEP_KEY = 'athena_quick_notification_step_v1';
// A special Android permission opens Settings and does not return an activity
// result to Capacitor. Persisting only this explicit setup intent lets us
// finish enabling the already-requested Quick Athena when the person comes
// back, without asking them to press the same button a second time.
const OVERLAY_SETUP_PENDING_KEY = 'athena_quick_overlay_setup_pending_v1';

function completed() {
  try { return localStorage.getItem(SETUP_KEY) === 'complete'; } catch { return false; }
}

function complete() {
  try { localStorage.setItem(SETUP_KEY, 'complete'); } catch { /* no-op */ }
}

function notificationStepHandled() {
  try { return localStorage.getItem(NOTIFICATION_STEP_KEY) === 'complete'; } catch { return false; }
}

function completeNotificationStep() {
  try { localStorage.setItem(NOTIFICATION_STEP_KEY, 'complete'); } catch { /* no-op */ }
}

function overlaySetupPending() {
  try { return localStorage.getItem(OVERLAY_SETUP_PENDING_KEY) === 'true'; } catch { return false; }
}

function markOverlaySetupPending() {
  try { localStorage.setItem(OVERLAY_SETUP_PENDING_KEY, 'true'); } catch { /* no-op */ }
}

function clearOverlaySetupPending() {
  try { localStorage.removeItem(OVERLAY_SETUP_PENDING_KEY); } catch { /* no-op */ }
}

function Button({ children, secondary = false, ...props }) {
  return (
    <button
      type="button"
      {...props}
      style={{
        width: '100%', minHeight: 44, padding: '11px 14px', borderRadius: 13,
        border: secondary ? '1px solid rgba(148,163,184,.26)' : '1px solid rgba(96,165,250,.65)',
        background: secondary ? 'rgba(15,23,42,.62)' : 'linear-gradient(135deg,#2563eb,#4f46e5)',
        color: '#eff6ff', fontSize: 14, fontWeight: 700, cursor: props.disabled ? 'not-allowed' : 'pointer',
        opacity: props.disabled ? .58 : 1,
      }}
    >{children}</button>
  );
}

/**
 * First native-login setup. Every operating-system permission remains behind
 * an explicit click; returning from Settings is rechecked instead of assumed.
 */
export function QuickAthenaSetup({ active = false }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState('notifications');
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('');

  const refresh = useCallback(async () => {
    if (!active || !isAndroid || completed()) return;
    const [notification, overlay] = await Promise.all([
      getAthenaNotificationStatus().catch(() => ({ permission: 'unknown' })),
      getAthenaFloatingOverlayStatus().catch(() => ({ available: false })),
    ]);
    if (!overlay?.available) return;
    if (overlay.enabled && overlay.permissionGranted) {
      clearOverlaySetupPending();
      complete();
      return;
    }
    // The user has already pressed “Configurar Quick Athena”; Android's
    // Settings screen is the only remaining confirmation. Once it is granted,
    // resume the requested setup automatically on returning to Athena.
    if (overlay.permissionGranted && overlaySetupPending()) {
      setBusy(true);
      try {
        const status = await enableAthenaFloatingOverlay({ visualState: 'idle' });
        if (status.enabled || status.running) {
          await setAthenaFloatingOverlayInteractionLevel('medium');
          clearOverlaySetupPending();
          complete();
          setOpen(false);
          return;
        }
        setNotice('O acesso foi concedido, mas a Quick Athena não iniciou. Tente novamente nas configurações.');
      } catch {
        setNotice('O acesso foi concedido, mas a Quick Athena não pôde iniciar agora. Tente novamente nas configurações.');
      } finally {
        setBusy(false);
      }
    }
    // Do not send someone back to the notification question after they have
    // already chosen “continuar sem”. The overlay access is the remaining
    // setup step, so returning from Android Settings must go straight there.
    setStep(notification?.permission === 'granted' || notificationStepHandled()
      ? 'overlay'
      : 'notifications');
    setOpen(true);
  }, [active]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => {
    if (!open) return undefined;
    const onFocus = () => { void refresh(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [open, refresh]);

  if (!open || !isAndroid || !isAthenaFloatingOverlayAvailable()) return null;

  const askNotifications = async () => {
    setBusy(true); setNotice('');
    try {
      const permission = await requestAthenaNotificationsPermission();
      completeNotificationStep();
      if (permission !== 'granted') {
        setNotice('As notificações ficaram desativadas. Você poderá ativá-las depois nas configurações do Android.');
      }
      setStep('overlay');
    } catch {
      setNotice('Não foi possível solicitar notificações agora. Você pode continuar sem elas.');
    } finally { setBusy(false); }
  };

  const askOverlay = async () => {
    setBusy(true); setNotice('');
    try {
      // This persisted flag represents this exact button press, not a
      // background permission request. Android opens its special page and the
      // refresh above finishes only after the person returns having granted it.
      markOverlaySetupPending();
      const permission = await requestAthenaFloatingOverlayPermission();
      if (!permission.permissionGranted) {
        setNotice('O Android ainda não concedeu “Exibir sobre outros apps”. Ative a chave na tela aberta e volte para a Athena.');
        return;
      }
      const status = await enableAthenaFloatingOverlay({ visualState: 'idle' });
      if (status.enabled || status.running) {
        await setAthenaFloatingOverlayInteractionLevel('medium');
        clearOverlaySetupPending(); complete(); setOpen(false);
      } else setNotice('A permissão foi concedida, mas a bolha não iniciou. Você pode tentar novamente nas configurações.');
    } catch {
      setNotice('Não foi possível preparar a Athena flutuante agora. Tente novamente nas configurações.');
    } finally { setBusy(false); }
  };

  // “Agora não” cancels any prior setup intent. The guided setup can be
  // offered again on a later Android entry; only the explicit setup button
  // can authorize a post-Settings automatic finish.
  const finishWithoutOverlay = () => { clearOverlaySetupPending(); setOpen(false); };
  const notificationStep = step === 'notifications';

  return (
    <div role="dialog" aria-modal="true" aria-label="Preparar Quick Athena" style={{
      position: 'fixed', inset: 0, zIndex: 100100, display: 'grid', placeItems: 'center', padding: 20,
      background: 'rgba(2,6,23,.78)', backdropFilter: 'blur(12px)',
    }}>
      <section style={{ width: 'min(100%, 420px)', padding: 24, borderRadius: 22, color: '#eaf3ff',
        border: '1px solid rgba(96,165,250,.32)', background: 'linear-gradient(145deg,#0a1932,#050b1c)',
        boxShadow: '0 28px 72px rgba(0,0,0,.58)' }}>
        <div style={{ color: '#93c5fd', fontSize: 12, fontWeight: 800, letterSpacing: '.12em' }}>QUICK ATHENA</div>
        <h2 style={{ margin: '9px 0 8px', fontSize: 22 }}>Prepare a Athena no seu celular</h2>
        {notificationStep ? <>
          <p style={{ margin: 0, color: 'rgba(219,234,254,.75)', lineHeight: 1.55 }}>
            Primeiro, escolha se quer receber lembretes e retornos importantes. A Athena não ativará notificações sem sua confirmação.
          </p>
          <div style={{ marginTop: 18, display: 'grid', gap: 9 }}>
            <Button onClick={askNotifications} disabled={busy}>{busy ? 'Solicitando…' : 'Permitir notificações'}</Button>
            <Button secondary onClick={() => { completeNotificationStep(); setStep('overlay'); }} disabled={busy}>Continuar sem notificações</Button>
          </div>
        </> : <>
          <p style={{ margin: 0, color: 'rgba(219,234,254,.75)', lineHeight: 1.55 }}>
            A Quick Athena é uma bolha discreta que aparece quando você sai do app e abre uma janela rápida sobre o que você estiver usando. Para isso, o Android precisa do acesso especial “Exibir sobre outros apps”.
          </p>
          <p style={{ margin: '10px 0 0', color: 'rgba(191,219,254,.56)', fontSize: 12, lineHeight: 1.5 }}>
            A Athena abrirá a tela do Android, mas não pode conceder essa permissão por você. O nível inicial é Médio e pode ser alterado depois.
          </p>
          <div style={{ marginTop: 18, display: 'grid', gap: 9 }}>
            <Button onClick={askOverlay} disabled={busy}>{busy ? 'Abrindo Android…' : 'Configurar Quick Athena'}</Button>
            <Button secondary onClick={finishWithoutOverlay} disabled={busy}>Agora não</Button>
          </div>
        </>}
        {notice ? <div role="status" style={{ marginTop: 14, padding: '10px 11px', borderRadius: 12, color: '#fde68a', background: 'rgba(120,53,15,.22)', fontSize: 12, lineHeight: 1.5 }}>{notice}</div> : null}
      </section>
    </div>
  );
}

export default QuickAthenaSetup;
